import { useAtomValue, useSetAtom } from "jotai";
import { useFormatters } from "../../contexts/formatters";
import SlotComponent from "./SlotComponent";
import {
  currentCalendarMonthDateAtom,
  filterPricingGuidAtom,
  isGroupPricingPackagesAtom,
  listingGuidAtom,
  passengersAtom,
  selectedDateAtom,
  selectedPackageAtom,
} from "./store";
import SlotUnavailableMessage from "./SlotUnavailableMessage";
import { DayPackagePriceResult, PricingMode, Query } from "@repo/rq-api-public";
import { groupBy } from "../../utils/array";
import { keepPreviousData } from "@tanstack/react-query";
import {
  useBookingStartDateTime as useBookingStartDateTime,
  useDebounce,
} from "../hooks";
import { useListingQuery } from "../../api/hooks";
import { addMonths, endOfMonth, isSameDay, startOfMonth } from "date-fns";
import CalendarDaySlot from "./CalendarDaySlot";

export default function CharterSlots({
  handleNextStep,
}: {
  handleNextStep: () => void;
}) {
  const selectedDay = useAtomValue(selectedDateAtom);
  const listingGuid = useAtomValue(listingGuidAtom);
  const filterPricingGuid = useAtomValue(filterPricingGuidAtom);
  const isGroupPricingPackages = useAtomValue(isGroupPricingPackagesAtom);
  const month = useAtomValue(currentCalendarMonthDateAtom);

  const { data: listing } = useListingQuery();
  const isCalendarMode = listing?.pricingMode === PricingMode.Calendar;
  const isPackagesMode = listing?.pricingMode === PricingMode.Packages;

  const { data: calendarDaySeasons } =
    Query.useListingsGuidCalendarPricingGETQuery(
      {
        guid: listingGuid!,
        startDate: startOfMonth(month),
        endDate: endOfMonth(addMonths(month, 1)),
      },
      {
        enabled: isCalendarMode && !!listingGuid,
        placeholderData: keepPreviousData,
      },
    );

  const { data: calendarDayPackages } =
    Query.useListingsGuidCalendarDayPricingsGETQuery(
      {
        guid: listingGuid!,
        date: selectedDay,
      },
      {
        enabled: isPackagesMode && !!listingGuid,
        placeholderData: keepPreviousData,
      },
    );

  const currency = listing?.currency || "USD";
  const locale = listing?.culture || "en-US";

  const allSlotsUnavailable = isPackagesMode
    ? calendarDayPackages?.dayPackagePrices?.every(
        (packagePricing) => !packagePricing.isAvailable,
      )
    : calendarDaySeasons?.dayPrices?.every((season) => !season.isAvailable);

  const groupedDayPackagePrices = groupBy(
    calendarDayPackages?.dayPackagePrices ?? [],
    (packagePricing) => packagePricing.pricingGuid!,
  );

  const season = calendarDaySeasons?.dayPrices?.find((dayPrice) =>
    isSameDay(dayPrice.date, selectedDay),
  );

  return (
    <div className="widget-slots">
      <ul className="widget-slots__list flex flex-col gap-4">
        {isPackagesMode && (
          <>
            {isGroupPricingPackages &&
              filterPricingGuid &&
              groupedDayPackagePrices?.[filterPricingGuid]?.map(
                (packagePricing) => (
                  <li key={packagePricing.guid} className="widget-slots__item">
                    <PackageSlot
                      packagePricing={packagePricing}
                      handleNextStep={handleNextStep}
                      currency={currency}
                      locale={locale}
                    />
                  </li>
                ),
              )}

            {!isGroupPricingPackages &&
              calendarDayPackages?.dayPackagePrices?.map((packagePricing) => (
                <li key={packagePricing.guid} className="widget-slots__item">
                  <PackageSlot
                    packagePricing={packagePricing}
                    handleNextStep={handleNextStep}
                    currency={currency}
                    locale={locale}
                  />
                </li>
              ))}
          </>
        )}
        {isCalendarMode && season && (
          <li className="widget-slots__item">
            <CalendarDaySlot
              season={season}
              selectedDate={selectedDay}
              handleNextStep={handleNextStep}
              currency={currency}
              locale={locale}
            />
          </li>
        )}
      </ul>
      {allSlotsUnavailable && <SlotUnavailableMessage />}
    </div>
  );
}

const PackageSlot = ({
  handleNextStep,
  packagePricing,
  currency,
  locale,
}: {
  packagePricing: DayPackagePriceResult;
  handleNextStep: () => void;
  currency?: string;
  locale?: string;
}) => {
  const { dateFormatter } = useFormatters();
  const passengers = useAtomValue(passengersAtom);
  const debouncedPassengers = useDebounce(passengers);
  const setSelectedPackage = useSetAtom(selectedPackageAtom);
  const startDateTime = useBookingStartDateTime();

  const { startTime, duration, pricingGuid, isAvailable } = packagePricing;

  const { data: calculation, isLoading } = Query.usePricingsCalculateGETQuery(
    {
      passengers: debouncedPassengers ?? undefined,
      pricingGuid,
      promoCodeRequest_StartDateTime: startDateTime,
    },
    {
      enabled: !!pricingGuid,
    },
  );

  if (!startTime || !duration || !pricingGuid) {
    return null;
  }

  return (
    <SlotComponent
      title={packagePricing.name!}
      fromToTime={dateFormatter.formatClockRange(startTime, duration)}
      price={calculation?.totalPriceWithoutOnlineCustomerFee}
      handleBook={() => {
        setSelectedPackage(packagePricing);
        handleNextStep();
      }}
      disabled={!isAvailable}
      loading={isLoading}
      currency={currency}
      locale={locale}
    />
  );
};
